<script setup lang="ts">
import { z } from 'zod';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';

definePageMeta({
	layout: 'default',
});

const route = useRoute();
const router = useRouter();
const { resetPassword } = useAuth();

const token = computed(() => route.query.token as string | undefined);
const passwordReset = ref(false);
const error = ref<string | null>(null);

if (!token.value) {
	error.value = 'Token di reset non valido o mancante';
}

const schema = z.object({
	password: z.string().min(8, 'La password deve essere di almeno 8 caratteri'),
	confirmPassword: z.string().min(8, 'Conferma password obbligatoria'),
}).refine((data) => data.password === data.confirmPassword, {
	message: 'Le password non corrispondono',
	path: ['confirmPassword'],
});

const { handleSubmit, isSubmitting, errors, defineField } = useForm({
	validationSchema: toTypedSchema(schema),
});

const [password, passwordAttrs] = defineField('password');
const [confirmPassword, confirmPasswordAttrs] = defineField('confirmPassword');

const onSubmit = handleSubmit(async (values) => {
	if (!token.value) {
		error.value = 'Token di reset non valido';
		return;
	}

	error.value = null;
	const result = await resetPassword(token.value, values.password);
	
	if (result.success) {
		passwordReset.value = true;
		setTimeout(() => {
			router.push('/login');
		}, 3000);
	} else {
		error.value = result.error || 'Errore durante il reset della password';
	}
});
</script>

<template>
	<div class="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
		<div class="max-w-md w-full space-y-8">
			<div>
				<h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900">
					Reimposta la tua password
				</h2>
			</div>

			<div v-if="passwordReset" class="rounded-md bg-green-50 p-4">
				<div class="flex">
					<div class="flex-shrink-0">
						<svg
							class="h-5 w-5 text-green-400"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fill-rule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
								clip-rule="evenodd"
							/>
						</svg>
					</div>
					<div class="ml-3">
						<p class="text-sm font-medium text-green-800">
							Password reimpostata con successo! Verrai reindirizzato al login tra pochi secondi...
						</p>
					</div>
				</div>
			</div>

			<form v-else-if="token" class="mt-8 space-y-6" @submit="onSubmit">
				<div v-if="error" class="rounded-md bg-red-50 p-4">
					<div class="flex">
						<div class="flex-shrink-0">
							<svg
								class="h-5 w-5 text-red-400"
								viewBox="0 0 20 20"
								fill="currentColor"
							>
								<path
									fill-rule="evenodd"
									d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
									clip-rule="evenodd"
								/>
							</svg>
						</div>
						<div class="ml-3">
							<p class="text-sm font-medium text-red-800">{{ error }}</p>
						</div>
					</div>
				</div>

				<div class="space-y-4">
					<div>
						<label for="password" class="block text-sm font-medium text-gray-700">
							Nuova Password
						</label>
						<input
							id="password"
							v-model="password"
							v-bind="passwordAttrs"
							type="password"
							autocomplete="new-password"
							class="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
							placeholder="Minimo 8 caratteri"
						/>
						<p v-if="errors.password" class="mt-1 text-sm text-red-600">
							{{ errors.password }}
						</p>
					</div>
					<div>
						<label for="confirmPassword" class="block text-sm font-medium text-gray-700">
							Conferma Password
						</label>
						<input
							id="confirmPassword"
							v-model="confirmPassword"
							v-bind="confirmPasswordAttrs"
							type="password"
							autocomplete="new-password"
							class="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
							placeholder="Conferma la nuova password"
						/>
						<p v-if="errors.confirmPassword" class="mt-1 text-sm text-red-600">
							{{ errors.confirmPassword }}
						</p>
					</div>
				</div>

				<div>
					<button
						type="submit"
						:disabled="isSubmitting"
						class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<span v-if="!isSubmitting">Reimposta password</span>
						<span v-else>Reset in corso...</span>
					</button>
				</div>

				<div class="text-center">
					<NuxtLink
						to="/login"
						class="font-medium text-primary hover:text-primary/80"
					>
						Torna al login
					</NuxtLink>
				</div>
			</form>

			<div v-else class="rounded-md bg-red-50 p-4">
				<div class="flex">
					<div class="flex-shrink-0">
						<svg
							class="h-5 w-5 text-red-400"
							viewBox="0 0 20 20"
							fill="currentColor"
						>
							<path
								fill-rule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
								clip-rule="evenodd"
							/>
						</svg>
					</div>
					<div class="ml-3">
						<p class="text-sm font-medium text-red-800">
							Token di reset non valido o mancante
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>


